import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { useContent } from '../../hooks/useContent';

export default function Preloader() {
  const imgRef = useRef<HTMLImageElement>(null);
  const ringRef = useRef<SVGSVGElement>(null);
  const { content } = useContent();
  const l = content?.logo;

  useEffect(() => {
    if (imgRef.current) {
      gsap.to(imgRef.current, { rotation: 360, duration: 8, ease: 'none', repeat: -1, transformOrigin: '50% 50%' });
    }
    if (ringRef.current) {
      gsap.to(ringRef.current, { rotation: -360, duration: 5, ease: 'none', repeat: -1, transformOrigin: '50% 50%' });
    }
  }, []);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'var(--navy-dark)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 24
      }}
    >
      {/* Logo image with outer ring */}
      <div style={{ position: 'relative', width: 120, height: 120 }}>
        {/* Rotating outer ring */}
        <svg ref={ringRef} width="120" height="120" viewBox="0 0 120 120" fill="none"
          style={{ position: 'absolute', inset: 0 }}>
          <circle cx="60" cy="60" r="56" stroke="#E8871A" strokeWidth="1" strokeDasharray="8 5" fill="none" opacity="0.5" />
          {[0,45,90,135,180,225,270,315].map((angle, i) => (
            <circle key={i}
              cx={60 + 54 * Math.cos((angle - 90) * Math.PI / 180)}
              cy={60 + 54 * Math.sin((angle - 90) * Math.PI / 180)}
              r="2" fill={i % 2 === 0 ? '#E8871A' : '#C9547A'} opacity="0.8" />
          ))}
        </svg>

        {/* Actual logo image — slow spin */}
        <img
          ref={imgRef}
          src="/vaastu-icon.png"
          alt="The Perfect Vaastu"
          style={{
            position: 'absolute', inset: 10,
            width: 100, height: 100,
            objectFit: 'contain',
            borderRadius: '50%',
            border: '1.5px solid rgba(232,135,26,0.4)',
            boxShadow: '0 0 24px rgba(232,135,26,0.4)',
            background: '#0D0D1F',
          }}
        />
      </div>

      {/* Text */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        style={{ textAlign: 'center' }}
      >
        <p style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, color: '#E8871A', letterSpacing: 2 }}>
          {l?.name ?? 'The Perfect Vaastu'}
        </p>
        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#FDF6EC', opacity: 0.5, letterSpacing: 4, marginTop: 6 }}>
          HARMONISING SPACES
        </p>
      </motion.div>

      {/* Progress bar */}
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: 160 }}
        transition={{ duration: 2.2, ease: 'easeInOut' }}
        style={{ height: 2, background: 'linear-gradient(90deg, transparent, #E8871A, transparent)', borderRadius: 2 }}
      />
    </motion.div>
  );
}
